/**
 * OverviewTab — a source's headline numbers, description, provenance and consent.
 * Template example: demo `/connected-apps` (an app's details: description card, facts list) and NextAdmin
 * `(dashboard)/(home)/_component/overview-stats` for the numbers.
 */
import React from 'react';
import { format } from 'date-fns';
import type { IntelSource } from '../../../types/intel-source.js';
import { StatStrip, Callout, Card } from '../../../components/ui';
import type { StatStripCell } from '../../../components/ui';
import { ConsentPanel } from './ConsentPanel.js';
import { formatRelativeTime } from './SourceCard.js';
import { Info } from 'lucide-react';

interface OverviewTabProps {
  source: IntelSource;
}

function originLabel(source: IntelSource): string {
  if (source.seed) return 'Seed';
  if (source.discoveredAt) return 'Discovered';
  return 'Added by hand';
}

function FactRow({ label, value, mono = false }: { label: string; value: React.ReactNode; mono?: boolean }) {
  return (
    <div className="grid grid-cols-[110px_minmax(0,1fr)] gap-3 py-2 text-sm">
      <dt className="text-muted">{label}</dt>
      <dd className={`min-w-0 break-words text-body ${mono ? 'font-mono text-xs' : ''}`}>{value}</dd>
    </div>
  );
}

const DASH = <span className="text-faint">—</span>;

export const OverviewTab: React.FC<OverviewTabProps> = ({ source }) => {
  const sq = source.signalQuality == null ? Number.NaN : parseFloat(source.signalQuality);
  const sqValid = Number.isFinite(sq) && sq > 0;
  const sqDisplay = sqValid ? Math.round(sq * 100) : null;

  const totalSig = source.totalSignals ?? 0;
  const correctSig = source.correctSignals ?? 0;
  const hitRatePct = totalSig > 0 ? Math.round((correctSig / totalSig) * 100) : null;

  const harvested = source.totalContentHarvested ?? 0;
  const strategies = source.totalStrategiesExtracted ?? 0;
  const allMetricsMissing = !sqValid && totalSig === 0 && harvested === 0 && strategies === 0;

  const cells: StatStripCell[] = [
    {
      label: 'Signal quality',
      value: sqDisplay !== null ? `${sqDisplay} / 100` : DASH,
      severity: sqValid ? (sq >= 0.7 ? 'success' : sq >= 0.4 ? 'warning' : 'danger') : undefined,
    },
    {
      label: 'Hit rate',
      value: hitRatePct !== null ? `${hitRatePct}%` : DASH,
      sub: totalSig > 0 ? `${correctSig} of ${totalSig} calls` : undefined,
    },
    {
      label: 'Content harvested',
      value: harvested > 0 ? harvested.toLocaleString() : DASH,
      sub: harvested > 0 ? 'last 30 days' : undefined,
    },
    {
      label: 'Strategies extracted',
      value: strategies > 0 ? strategies.toLocaleString() : DASH,
      sub: strategies > 0 ? 'from this source' : undefined,
    },
  ];

  return (
    <div className="space-y-5">
      {allMetricsMissing && (
        <Callout tone="accent" icon={Info} title="Scores not computed yet">
          This source has no signal-quality, hit-rate or harvest counts yet. The numbers below show a dash until the
          enrichment job has run.
        </Callout>
      )}

      <StatStrip cells={cells} className="mb-0" />

      <div className="grid grid-cols-1 items-start gap-5 xl:grid-cols-[minmax(0,1fr)_340px]">
        <div className="min-w-0 space-y-5">
          <Card title="Description">
            <p className="text-sm leading-relaxed text-body">
              {source.description ?? <span className="text-faint">No description.</span>}
            </p>
          </Card>
          <ConsentPanel source={source} />
        </div>

        <Card title="Provenance">
          <dl className="divide-y divide-border">
            <FactRow
              label="Added"
              value={`${originLabel(source)} · ${format(new Date(source.createdAt), 'MMM yyyy')}`}
            />
            <FactRow label="External id" value={source.externalId ?? DASH} mono />
            <FactRow label="Chain" value={source.chain ?? DASH} />
            <FactRow
              label="Last content"
              value={source.lastContentAt ? formatRelativeTime(source.lastContentAt) : 'Never'}
            />
          </dl>
        </Card>
      </div>
    </div>
  );
};
